import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { defaultTargetingBuilderFilter, type TargetingStatus } from './model';
import {
  parseTargetingBuilderRouteSearch,
  serializeTargetingBuilderRouteSearch,
  type TargetingBuilderRouteFilters,
  type TargetingBuilderRouteSearch,
} from './targetingBuilderRouteSearch';

export function useTargetingBuilderRouteFilters() {
  const navigate = useNavigate({ from: '/targeting/' });
  const search = useSearch({ from: '/targeting/' });
  const filters = useMemo(() => parseTargetingBuilderRouteSearch(search), [search]);

  const updateFilters = useCallback(
    (updater: (current: TargetingBuilderRouteFilters) => TargetingBuilderRouteFilters) => {
      void navigate({
        search: (current: TargetingBuilderRouteSearch) =>
          serializeTargetingBuilderRouteSearch(updater(parseTargetingBuilderRouteSearch(current))),
        replace: true,
      });
    },
    [navigate],
  );

  return {
    filters,
    queryFilter: { ...defaultTargetingBuilderFilter, ...filters },
    setSearch: useCallback(
      (searchTerm: string) => {
        updateFilters((current) => ({
          ...current,
          searchTerm,
          page: defaultTargetingBuilderFilter.page,
        }));
      },
      [updateFilters],
    ),
    setStatuses: useCallback(
      (statuses: TargetingStatus[]) => {
        updateFilters((current) => ({
          ...current,
          statuses,
          page: defaultTargetingBuilderFilter.page,
        }));
      },
      [updateFilters],
    ),
    setPagination: useCallback(
      (page: number, size: number) => {
        updateFilters((current) => ({ ...current, page, size }));
      },
      [updateFilters],
    ),
  };
}
